if (!customElements.get('bigboy-sticky-atc')) {
  customElements.define(
    'bigboy-sticky-atc',
    class BigBoyStickyAtc extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.stickyButton = this.querySelector('[data-sticky-atc-button]');
        this.stickyButtonText = this.stickyButton?.querySelector('span');
        this.stickyPrice = this.querySelector('[data-sticky-atc-price]');
        this.originalButtonPassed = false;
        this.originalButtonWasSeen = false;

        this.handleStickyClick = this.handleStickyClick.bind(this);
        this.handleMediaChange = this.handleMediaChange.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
        this.handleDocumentInteraction = this.handleDocumentInteraction.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo || !this.stickyButton) return;

        this.stickyButton.addEventListener('click', this.handleStickyClick);
        this.mobileQuery.addEventListener('change', this.handleMediaChange);
        window.addEventListener('resize', this.handleViewportChange, { passive: true });
        document.addEventListener('click', this.handleDocumentInteraction, true);
        document.addEventListener('keyup', this.handleDocumentInteraction, true);

        this.bodyObserver = new MutationObserver(() => this.refreshVisibility());
        this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.sectionId) return;
            this.connectToOriginalControls();
          });
        }

        this.connectToOriginalControls();
      }

      disconnectedCallback() {
        this.stickyButton?.removeEventListener('click', this.handleStickyClick);
        this.mobileQuery?.removeEventListener('change', this.handleMediaChange);
        window.removeEventListener('resize', this.handleViewportChange);
        window.removeEventListener('scroll', this.handleViewportChange);
        document.removeEventListener('click', this.handleDocumentInteraction, true);
        document.removeEventListener('keyup', this.handleDocumentInteraction, true);
        this.buttonObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        this.bodyObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        document.body.classList.remove('bigboy-sticky-atc-visible');
      }

      connectToOriginalControls() {
        const originalButton = this.productInfo.querySelector('.product-form__submit');
        if (!originalButton) {
          this.originalButton = null;
          this.originalButtonPassed = false;
          this.refreshVisibility();
          return;
        }

        if (this.originalButton !== originalButton) {
          this.originalButton = originalButton;
          this.originalButtonPassed = false;
          this.originalButtonWasSeen = false;
          this.observeOriginalButton();
        }

        this.syncFromOriginalControls();
        this.refreshVisibility();
      }

      observeOriginalButton() {
        this.buttonObserver?.disconnect();
        this.intersectionObserver?.disconnect();

        this.buttonObserver = new MutationObserver(() => this.syncFromOriginalControls());
        this.buttonObserver.observe(this.originalButton, {
          attributes: true,
          attributeFilter: ['aria-disabled', 'class', 'disabled'],
          childList: true,
          subtree: true,
        });

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;

            if (entry.isIntersecting) {
              this.originalButtonWasSeen = true;
              this.originalButtonPassed = false;
            } else if (entry.boundingClientRect.bottom <= 0) {
              this.originalButtonWasSeen = true;
              this.originalButtonPassed = true;
            } else if (entry.boundingClientRect.top >= window.innerHeight) {
              this.originalButtonPassed = false;
            }

            this.refreshVisibility();
          });
          this.intersectionObserver.observe(this.originalButton);
        } else {
          window.addEventListener('scroll', this.handleViewportChange, { passive: true });
          this.handleViewportChange();
        }
      }

      syncFromOriginalControls() {
        if (!this.originalButton) return;

        const sourcePriceWrapper = this.productInfo.querySelector(`#price-${this.sectionId}`);
        const sourcePrice = sourcePriceWrapper?.querySelector('.price');
        const priceUnavailable =
          !sourcePriceWrapper ||
          sourcePriceWrapper.hidden ||
          sourcePriceWrapper.classList.contains('hidden') ||
          !sourcePrice;

        if (this.stickyPrice) {
          this.stickyPrice.hidden = priceUnavailable;
          this.stickyPrice.innerHTML = priceUnavailable ? '' : sourcePrice.outerHTML;
        }

        const sourceText = this.originalButton.querySelector('span')?.textContent.trim();
        if (sourceText && this.stickyButtonText) this.stickyButtonText.textContent = sourceText;

        const loading = this.originalButton.classList.contains('loading');
        const disabled =
          this.originalButton.disabled ||
          this.originalButton.getAttribute('aria-disabled') === 'true' ||
          loading;

        this.stickyButton.disabled = disabled;
        if (disabled) {
          this.stickyButton.setAttribute('aria-disabled', 'true');
        } else {
          this.stickyButton.removeAttribute('aria-disabled');
        }
        if (loading) {
          this.stickyButton.setAttribute('aria-busy', 'true');
        } else {
          this.stickyButton.removeAttribute('aria-busy');
        }
        this.stickyButton.classList.toggle('loading', loading);
        this.stickyButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      handleStickyClick() {
        this.connectToOriginalControls();
        if (
          !this.originalButton ||
          this.originalButton.disabled ||
          this.originalButton.getAttribute('aria-disabled') === 'true'
        ) {
          return;
        }

        this.originalButton.click();
        requestAnimationFrame(() => this.syncFromOriginalControls());
      }

      handleMediaChange() {
        if (this.mobileQuery.matches) {
          this.handleViewportChange();
        } else {
          this.originalButtonPassed = false;
          this.originalButtonWasSeen = false;
          this.refreshVisibility();
        }
      }

      handleViewportChange() {
        if (!this.originalButton || !this.mobileQuery.matches) {
          this.refreshVisibility();
          return;
        }

        const rect = this.originalButton.getBoundingClientRect();
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          this.originalButtonWasSeen = true;
          this.originalButtonPassed = false;
        } else if (rect.bottom <= 0) {
          this.originalButtonWasSeen = true;
          this.originalButtonPassed = true;
        } else if (rect.top >= window.innerHeight) {
          this.originalButtonPassed = false;
        }

        this.refreshVisibility();
      }

      handleDocumentInteraction() {
        requestAnimationFrame(() => this.refreshVisibility());
      }

      hasOpenOverlay() {
        return Boolean(
          document.body.classList.contains('overflow-hidden') ||
            document.body.classList.contains('overflow-hidden-mobile') ||
            document.body.classList.contains('overflow-hidden-tablet') ||
            document.querySelector(
              'header-drawer details[open], dialog[open], product-modal[open], modal-dialog[open], pickup-availability-drawer[open], cart-drawer.active'
            )
        );
      }

      refreshVisibility() {
        const shouldShow =
          this.mobileQuery.matches &&
          this.originalButtonWasSeen &&
          this.originalButtonPassed &&
          !this.hasOpenOverlay();

        this.hidden = !shouldShow;
        document.body.classList.toggle('bigboy-sticky-atc-visible', shouldShow);
      }
    }
  );
}
